import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { DynamicDialogRef, DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog';
import { Table, TableLazyLoadEvent, TableRowSelectEvent } from 'primeng/table';
import { BehaviorSubject, debounceTime, distinctUntilChanged } from 'rxjs';
import { AssetManagementControllerGetAll$Params } from 'src/app/api/fn/asset-management/asset-management-controller-get-all';
import { AssetManagementItem, ReplacementAssetManagementCreateDto } from 'src/app/api/models';
import { AssetManagementService } from 'src/app/api/services';
import { manipulateToUTCISOString } from 'src/main';

@Component({
  selector: 'app-modal-reverse-replace-asset-management',
  templateUrl: './modal-reverse-replace-asset-management.component.html',
  styleUrls: ['./modal-reverse-replace-asset-management.component.scss'],
})
export class ModalReverseReplaceAssetManagementComponent {
  @ViewChild('assetTable') assetTable?: Table;

  protected readonly rowsPerPageOptions = [25, 50, 100, 200, 500];
  availableAssetsManagement: AssetManagementItem[] = [];
  totalRecords = 0;
  question_title: string;
  selectedAM = new FormControl<AssetManagementItem | null>(null);
  /** L'asset corrente (B): quello che è il sostituto */
  amCurrent?: AssetManagementItem;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public dialogService: DialogService,
    private amService: AssetManagementService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private router: Router
  ) {
    this.question_title = config.data.question_title;
    this.amCurrent = config.data.amCurrent;
  }

  frmFilter = new FormGroup({
    part_number: new FormControl<string | undefined>(undefined),
    serial_number: new FormControl<string | undefined>(undefined),
    asset_code: new FormControl<string | undefined>(undefined),
    notes: new FormControl<string | undefined>(undefined),
    active: new FormControl<boolean>(true),
    lost: new FormControl<boolean>(false),
    faulty: new FormControl<boolean>(false),
    end_of_warranty_date: new FormControl<[Date, Date] | undefined>(undefined),
    testing_date: new FormControl<[Date, Date] | undefined>(undefined),
  });

  ngOnInit() {
    this.frmFilter.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.fetchAssetsManagements(this.assetTable?.createLazyLoadMetadata());
    });
  }

  onAssetSelect(event: TableRowSelectEvent) {
    this.selectedAM.setValue(event.data);
  }

  close() {
    this.ref.close();
  }

  save() {
    this.confirmationService.confirm({
      message: `Confermi che l'asset n. <strong>${this.amCurrent?.serial_number}</strong> sostituisce l'asset n. <strong>${this.selectedAM.value?.serial_number}</strong>?`,
      header: 'Attenzione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sì',
      acceptButtonStyleClass: 'bg-danger',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        const body: ReplacementAssetManagementCreateDto = {
          asset_management_to_replace_id: this.selectedAM.value?.id!,
          replacement_asset_management_id: this.amCurrent?.id!,
        };

        this.amService.assetManagementControllerReplacementAssetManagement({ body }).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Aggiornamento',
              detail: `Sostituzione effettuata.`,
            });
            this.ref.close(true);
            this.router.navigateByUrl('/main/asset');
          },
        });
      },
    });
  }

  fetchAssetsManagements(event: TableLazyLoadEvent) {
    const frmValue = this.frmFilter.value;
    const end_of_warranty_date_from = frmValue.end_of_warranty_date
      ? manipulateToUTCISOString(frmValue.end_of_warranty_date[0], false)
      : undefined;
    const end_of_warranty_date_to = frmValue.end_of_warranty_date
      ? manipulateToUTCISOString(frmValue.end_of_warranty_date[1], true, true)
      : undefined;

    const testing_date_from = frmValue.testing_date
      ? manipulateToUTCISOString(frmValue.testing_date[0], false)
      : undefined;
    const testing_date_to = frmValue.testing_date
      ? manipulateToUTCISOString(frmValue.testing_date[1], true, true)
      : undefined;

    const params: AssetManagementControllerGetAll$Params = {
      limit: +event.rows!,
      skip: +event.first!,
      part_number: frmValue.part_number ?? undefined,
      serial_number: frmValue.serial_number ?? undefined,
      asset_code: frmValue.asset_code ?? undefined,
      notes: frmValue.notes ?? undefined,
      active: frmValue.active ?? undefined,
      lost: frmValue.lost ?? undefined,
      faulty: frmValue.faulty ?? undefined,
      end_of_warranty_date_from,
      end_of_warranty_date_to,
      testing_date_from,
      testing_date_to,
      // Mostra solo asset non ancora sostituiti, stesso contratto, escludi quello corrente
      not_replaced_by: true,
      show_only_asset_with_registry_end_customer_id: true,
      related_agreement_id: this.amCurrent?.related_agreement?.id ?? undefined,
      exclude_id: this.amCurrent?.id!,
    };

    this.amService.assetManagementControllerGetAll(params).subscribe({
      next: res => {
        this.availableAssetsManagement = res.data.list;
        this.totalRecords = res.data.count;
      },
    });
  }

  clear(table: Table) {
    this.frmFilter.reset({ active: true }, { emitEvent: false });
    table.reset();
  }
}
